import { getDownloader } from './Downloader';
import { ElementNode } from './ElementNode';
import type { CrossOrigin } from './Def';
export interface ScriptNodeInitOptions {
    owner?: string;
    src?: string;
    content?: string;
    async?: boolean;
    defer?: boolean;
    type?: string;
    crossOrigin?: CrossOrigin;
    entry?: boolean;
    noModule?: boolean;
}

export class ScriptNode extends ElementNode {
    #options: ScriptNodeInitOptions;

    constructor(options: ScriptNodeInitOptions) {
        super({ owner: options.owner || location.href });
        this.#options = options;
    }

    public override get [Symbol.toStringTag](): string {
        return 'ScriptNode';
    }

    public get isESM(): boolean {
        return this.#options.type === 'module';
    }

    public get crossOrigin(): CrossOrigin {
        return this.#options.crossOrigin;
    }

    public get isInline(): boolean {
        return !this.#options.src;
    }

    public get isEntry(): boolean {
        return this.#options.entry === true;
    }

    public get isAsync(): boolean {
        return this.#options.async === true;
    }

    public get isDefer(): boolean {
        // "defer" works only for external scripts, or ESM.
        return (this.#options.defer === true && !!this.src) || this.isESM;
    }

    public get src(): string | undefined {
        return this.#options.src?.trim();
    }

    public override get content(): string {
        return (this.#options.content || '').trim();
    }

    public downloadContent(options?: RequestInit): Promise<string> {
        if (!this.src) {
            return Promise.resolve(this.content);
        }

        // Do not download if ESM: we do not support invoke ESM source code.
        if (this.isESM) {
            return Promise.resolve(this.content);
        }

        const req: RequestInit = {
            ...options,
        };

        if (this.#options.crossOrigin === 'use-credentials') {
            req.credentials = 'include';
        }

        return getDownloader()
            .download(this.src, req)
            .then(text => {
                this.#options.content = text;
                return text;
            });
    }

    public override get isValid(): boolean {
        if (this.#options.noModule) {
            // We always enable ES module.
            return false;
        }

        if (!this.#options.src && !this.content) {
            return false;
        }

        if (!['', undefined, 'module', 'text/javascript'].includes(this.#options.type)) {
            return false;
        }

        return true;
    }

    public override toString(): string {
        return `<${[
            'script',
            this.src ? `src="${this.src}"` : '',
            this.isAsync ? 'async' : '',
            this.isEntry ? 'entry' : '',
            this.#options.defer ? 'defer' : '',
            this.#options.type ? `type="${this.#options.type}"` : '',
            this.#options.crossOrigin ? `crossorigin="${this.#options.crossOrigin}"` : '',
        ]
            .filter(Boolean)
            .join(' ')}>${this.content.length > 50 ? `${this.content.trim().slice(0, 50)}...` : this.content}</script>`;
    }
}
